<template>
  <div class="container">
    <HeadComponent/>

    <main>
      <form class="row justify-content-center align-items-center" @submit.prevent="login">
        <div class="w-25">
          <h2>Login</h2>
          <input class="form-control mb-2" type="email" v-model="email" placeholder="Email" required/>
          <input class="form-control mb-2" type="password" v-model="password" placeholder="Password" required/>
          <button class="btn btn-primary" type="submit">Login</button>
        </div>
      </form>
    </main>

    <FooterComponent/>
  </div>
</template>

<script>
import {eventBus} from '@/eventBus';

import {loginService} from '@/services/auth/loginService.js';

import HeadComponent from '@/components/views/head/HeadComponent.vue';
import FooterComponent from "@/components/views/footer/FooterComponent.vue";

export default {
  name: 'LoginView',
  components: {
    FooterComponent,
    HeadComponent,
  },
  /**
   * Returns an object that contains default user-related data.
   *
   * @return {Object} An object with the following properties:
   * - email {string}: The user's email, initialized as an empty string.
   * - password {string}: The user's password, initialized as an empty string.
   * - hasUserData {boolean}: A flag indicating if user data exists, initialized as false.
   * - name {string}: The user's name, initialized as an empty string.
   * - b_date {string}: The user's birth date, initialized as an empty string.
   */
  data() {
    return {
      email: '',
      password: '',
      hasUserData: false,
      name: '',
      b_date: '',
    };
  },
  /**
   * Lifecycle hook called after the component is mounted to the DOM.
   * It initializes the user data by calling `updateUserData` and sets up an event listener for session updates.
   *
   * @return {void} Does not return a value.
   */
  mounted() {
    this.updateUserData();
    eventBus.on('session-updated', this.updateUserData);
  },
  /**
   * Hook that is called right before the component is unmounted.
   * It performs cleanup operations, such as removing event listeners,
   * to prevent memory leaks.
   *
   * @return {void} Does not return any value.
   */
  beforeUnmount() {
    eventBus.off('session-updated', this.updateUserData);
  },
  /**
   * Contains methods for user login functionality and user data update.
   */
  methods: {
    /**
     * Authenticates the user with the provided email and password.
     * Updates the user data, displays a success message on successful login,
     * emits a session update event, and navigates to a specific route.
     *
     * @return {Promise<void>} Resolves when the login process completes,
     * or catches and handles errors appropriately.
     */
    async login() {
      try {
        await loginService(this.email, this.password);
        this.updateUserData();

        await this.$swal({
          icon: 'success',
          title: `Welcome back ${sessionStorage.getItem('name')}`,
          text: sessionStorage.getItem('message') || 'Login successful.',
          confirmButtonText: 'Accept',
        });

        eventBus.emit('session-updated');
        this.$router.push('/links');
      } catch (error) {
        console.error('Error logging in:', error);
        alert('Login failed. Please try again.');
      }
    },
    /**
     * Updates the user data fields by retrieving the values stored in session storage.
     * It checks if the user's name exists in session storage and updates the corresponding properties:
     * - `hasUserData`: Boolean indicating if user data is available.
     * - `name`: The user's name retrieved from session storage, defaults to an empty string if not found.
     * - `b_date`: The user's birth date retrieved from session storage, defaults to an empty string if not found.
     *
     * @return {void} This method does not return a value.
     */
    updateUserData() {
      this.hasUserData = !!sessionStorage.getItem('name');
      this.name = sessionStorage.getItem('name') || '';
      this.b_date = sessionStorage.getItem('b_date') || '';
    },
  },
};
</script>
